// useOrganismChoreography — how the organism moves as you navigate.
//
// BACKDROP MOVEMENT LAWS (Task 7) — strictly enforced here:
//   1. SINGLE-AXIS SLIDE for simple relocations — a move that changes only a row
//      OR only a column slides on that ONE axis (pure horizontal or pure vertical).
//   2. AGES/DE-AGES only on a TWO-AXIS (diagonal) relocation — the de-age/regrow
//      gesture (collapse to nucleus, then regrow) fires ONLY when both row AND
//      column change. Single-axis moves never de-age.
//   3. CONSISTENT ANCHORS — each section always lands at the same anchor
//      (SECTION_ANCHOR is a static map; no per-move drift).
//   4. NEVER FADE — the organism stays fully present at all times. The collapse is
//      a SCALE to the nucleus (not an opacity fade), and opacity is NEVER animated
//      during a relocation (the inner CSS transition no longer includes opacity).
//      The organism slides, or de-ages and regrows; it never fades out/in.
//
// Motion feel: the spin ACCUMULATES forward only — every grow nudges it a little
// further and it never resets, so it can't do the janky fast-spin-then-correct.
// The collapse is quick so the regrow begins as the shrink closes (small gap),
// and the grow uses a strong ease-in so the outer-petal spin builds as it opens.
// Continuous ambient spin lives in the engine loop (MemoryOrganism).

import { useEffect, useRef, useState } from "react";
import {
  ANCHOR_NUM,
  classify,
  focusForDepth,
  NETTI_Z,
  scaleForDepth,
  anchorFor,
  sectionFromPath,
  shiftAxis,
  VIEW_ANCHOR,
  type Section,
  type SectionAnchor,
} from "./organismChoreography";
import type { Anchor } from "../../components/OrganismBackdrop";
import type { OrganismFocus } from "../../components/MemoryOrganism";

const COLLAPSE_MS = 300; // shrink fast so the regrow begins as the shrink closes
const REGROW_MS = 660;
const SHIFT_MS = 1100;
const DIVE_MS = 760; // smooth grow when diving directory depth
const COLLAPSE_SCALE = 0.08;
const EASE_IN = "cubic-bezier(0.55, 0, 0.85, 0.3)"; // strong ease-in: spin builds as it grows
const EASE_SOFT = "var(--ease-soft)";
// forward-only spin increments (radians) — never reset, so the organism always
// turns a little as it grows and never corrects backward.
const SPIN_REGROW = 0.95;
const SPIN_SHIFT = 0.3;
const SPIN_DIVE = 0.32;
// a lateral move between sibling directories (same depth, within projects) spins
// the organism on its FIXED axis to bring a different sector forward — it never
// slides the origin (the camera no longer pans; see bloomField.focusLeaf).
const SPIN_SIBLING = 0.5;

type Phase = "idle" | "collapse" | "regrow" | "shift";

export interface OrganismChoreo {
  anchor: Anchor;
  offset?: { x?: string; y?: string };
  glide: boolean;
  glideAxis: "x" | "y" | "both" | "none";
  scale: number;
  opacity: number;
  innerTransMs: number;
  innerEase: string;
  rotation: number;
  focus: OrganismFocus | null;
  /** where in the page's stack netti sits on this section (SectionAnchor.z). */
  z: number;
}

export function useOrganismChoreography(
  path: string,
  opts: {
    depth: number;
    inViewer: boolean;
    activeIdx: number;
    reduced: boolean;
    expanded?: boolean;
    /** a VIEW with its own anchor (VIEW_ANCHOR: "timeline" · "repo" · "agents"), or
     *  null to keep the section's. */
    viewAnchor?: string | null;
  },
): OrganismChoreo {
  const { depth, inViewer, activeIdx, reduced } = opts;
  const baseSection = sectionFromPath(path);
  // A VIEW may seat netti somewhere of its own (VIEW_ANCHOR): the commit timeline
  // at CENTER-RIGHT (it scrubs the age like the activity log), the repo graph at
  // BOTTOM-LEFT (its lanes grow out of netti). Only a view that HAS an anchor
  // counts; we treat it as a distinct "section" for the move classifier so
  // entering/leaving it choreographs a proper relocation.
  const section: Section = baseSection;
  const viewAnchor = opts.viewAnchor && Object.prototype.hasOwnProperty.call(VIEW_ANCHOR, opts.viewAnchor) ? opts.viewAnchor : null;
  const sa: SectionAnchor = anchorFor(section, viewAnchor);

  // An anchored view is a flat list (no directory dive) — it must NOT inherit the
  // projects depth-grow/focus, which would balloon it center-screen.
  const steadyScale = viewAnchor ? (sa.scale ?? 1) : scaleForDepth(section, depth, inViewer);
  const steadyFocus = !viewAnchor && section === "projects" ? focusForDepth(depth, activeIdx) : null;
  const steadyOpacity = opts.expanded ? Math.min(sa.opacity, 0.5) : sa.opacity;

  // "view key" — the section, distinguished by the anchored view so entering /
  // leaving one (which keeps section=projects) still choreographs a real
  // relocation. We remember the previous anchor + offset directly because the
  // view anchors aren't in SECTION_ANCHOR. (Task 1.)
  const viewKey = viewAnchor ? `${section}::${viewAnchor}` : section;
  const prev = useRef<{ key: string; num: number; anchor: Anchor; offset?: { x?: string; y?: string }; first: boolean }>({
    key: viewKey,
    num: ANCHOR_NUM[sa.anchor],
    anchor: sa.anchor,
    offset: sa.offset,
    first: true,
  });
  const prevDepth = useRef(depth);
  const spinAccum = useRef(0); // monotonic forward spin (radians)
  const [, setSpinTick] = useState(0);
  const t1 = useRef<ReturnType<typeof setTimeout> | null>(null);
  const t2 = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [trans, setTrans] = useState<{
    phase: Phase;
    fromAnchor?: Anchor;
    fromOffset?: { x?: string; y?: string };
    glideAxis?: "x" | "y";
  }>({ phase: "idle" });

  // section / anchored-view change → collapse/regrow or single-axis shift
  useEffect(() => {
    const p = prev.current;
    if (p.key === viewKey) return;

    const fromN = p.num;
    const toN = ANCHOR_NUM[sa.anchor];
    const prevAnchor = p.anchor;
    const prevOffset = p.offset;
    if (t1.current) clearTimeout(t1.current);
    if (t2.current) clearTimeout(t2.current);

    if (p.first) {
      prev.current = { key: viewKey, num: toN, anchor: sa.anchor, offset: sa.offset, first: false };
      return;
    }
    const kind = classify(fromN, toN);
    prev.current = { key: viewKey, num: toN, anchor: sa.anchor, offset: sa.offset, first: false };

    if (reduced || kind === "none") {
      setTrans({ phase: "idle" });
      return;
    }
    if (kind === "shift") {
      spinAccum.current += SPIN_SHIFT;
      // PURE SINGLE-AXIS (Law 1): carry the SOURCE offset so the cross-axis offset
      // can be pinned to it during the slide — several section pairs differ on the
      // cross-axis offset (e.g. projects↔settings y, activity↔you x), which would
      // otherwise drift the "horizontal/vertical" slide diagonally via transform.
      setTrans({ phase: "shift", glideAxis: shiftAxis(fromN, toN), fromOffset: prevOffset });
      t1.current = setTimeout(() => setTrans({ phase: "idle" }), SHIFT_MS);
      return;
    }
    // diagonal → collapse to nucleus, snap (while invisible), regrow with a spin
    spinAccum.current += SPIN_REGROW;
    setTrans({ phase: "collapse", fromAnchor: prevAnchor, fromOffset: prevOffset });
    t1.current = setTimeout(() => {
      setTrans({ phase: "regrow" });
      t2.current = setTimeout(() => setTrans({ phase: "idle" }), REGROW_MS);
    }, COLLAPSE_MS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewKey]);

  // depth change within a section → a little forward spin as it grows/shrinks
  useEffect(() => {
    if (depth !== prevDepth.current) {
      prevDepth.current = depth;
      spinAccum.current += SPIN_DIVE;
      setSpinTick((t) => t + 1);
    }
  }, [depth]);

  // LATERAL move within projects (switch sibling folder/project at the SAME depth)
  // → spin on the fixed axis instead of sliding. Compared from the path alone so it
  // never depends on effect ordering with the depth/section effects above: a move
  // that keeps the same depth AND stays inside projects is a pure sibling switch.
  const prevPath = useRef(path);
  useEffect(() => {
    const from = prevPath.current;
    prevPath.current = path;
    if (from === path) return;
    const sameDepth = from.split("/").filter(Boolean).length === depth;
    const lateral = section === "projects" && sectionFromPath(from) === "projects" && sameDepth;
    if (lateral) {
      spinAccum.current += SPIN_SIBLING;
      setSpinTick((t) => t + 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path]);

  useEffect(
    () => () => {
      if (t1.current) clearTimeout(t1.current);
      if (t2.current) clearTimeout(t2.current);
    },
    [],
  );

  // ---- compose ----
  let anchor = sa.anchor;
  let offset = sa.offset;
  let glide = true;
  let glideAxis: "x" | "y" | "both" | "none" = "both";
  let scale = steadyScale;
  let opacity = steadyOpacity;
  let innerTransMs = DIVE_MS;
  let innerEase = EASE_SOFT;
  const rotation = spinAccum.current; // monotonic forward; engine eases toward it

  if (trans.phase === "collapse") {
    anchor = trans.fromAnchor ?? sa.anchor; // hold the SOURCE anchor while shrinking
    offset = trans.fromOffset;
    glide = false;
    scale = COLLAPSE_SCALE;
    // NEVER FADE (Law 4): the diagonal gesture DE-AGES via SCALE to the nucleus —
    // the organism stays fully present (opacity unchanged), it does not fade out.
    innerTransMs = COLLAPSE_MS;
    innerEase = EASE_IN;
  } else if (trans.phase === "regrow") {
    glide = false;
    innerTransMs = REGROW_MS;
    innerEase = EASE_IN;
  } else if (trans.phase === "shift") {
    // SINGLE-AXIS SLIDE (Law 1): glide on exactly one axis, no scale change, no
    // fade. Pin the CROSS-axis offset to the source so the animated transform can
    // only move along the sliding axis (no diagonal drift from a differing offset).
    glide = true;
    glideAxis = trans.glideAxis ?? "both";
    if (glideAxis === "x") {
      offset = { x: sa.offset?.x, y: trans.fromOffset?.y }; // y held = source → pure horizontal
    } else if (glideAxis === "y") {
      offset = { x: trans.fromOffset?.x, y: sa.offset?.y }; // x held = source → pure vertical
    }
  }

  return { anchor, offset, glide, glideAxis, scale, opacity, innerTransMs, innerEase, rotation, focus: steadyFocus, z: sa.z ?? NETTI_Z };
}
